/**
 * @fileoverview Canonical preview telemetry helper. Computes the same
 *               "what's been collected" counts that `getVisibleResponses`
 *               / `getAllViolations` produce on a real `InspectionCase`,
 *               but driven by the in-memory session state the canvas
 *               preview holds (`AllFormValues` + logged-violation count +
 *               contact-change count). The output is the contract the
 *               right-side data-collection panel renders; it is
 *               intentionally identical in shape to what tenant-web
 *               inspection completion and mobile-pwa inspection completion
 *               would compute from their real case document, so the
 *               preview reads like a real inspection in progress.
 * @module @firesquirrel/core-domain/inspection/previewTelemetry
 *
 * Responsibilities:
 * - Compute visible-filled / required-empty / hidden-form / hidden-section /
 *   hidden-field counts using the canonical form-scope rule engine plus
 *   the template-scope rule engine.
 * - Expose a single `PreviewTelemetry` record so every canvas preview
 *   consumer renders from the same data shape.
 *
 * Dependencies:
 * - `./rulesEngine` — `evaluateFormRules` (form-scope evaluator).
 * - `./templateRuleEngine` — `evaluateTemplateRules` (template-scope).
 * - `./types` — canonical form / rule types.
 *
 * Architecture:
 * - Pure / side-effect free. Callers pass snapshotted state; the helper
 *   returns a fresh `PreviewTelemetry` object. Safe to call inside
 *   `useMemo` in React consumers.
 * - `visibleFilledCount` counts a field once, considering both
 *   template-scope (`hiddenFields` + `hiddenSections` + `hiddenForms`)
 *   and form-scope (`ResolvedFormState`) visibility. A field whose
 *   parent section or form is hidden does NOT count as "visible filled",
 *   matching real-inspection semantics where such responses are
 *   `parentSectionIsHidden: true` or `isHidden: true`.
 *
 * Notes:
 * - Kept free of attachment / signature counting; those are Mode A/B/C
 *   concerns and do not flow through `AllFormValues`. Violation and
 *   contact counts are passed in by the caller since they live in
 *   separate module state during a preview session.
 */

import { evaluateFormRules } from './rulesEngine';
import { evaluateTemplateRules } from './templateRuleEngine';
import type {
  AllFormValues,
  TemplateRuleEvalResult,
} from './templateRuleEngine';
import type {
  Condition,
  FieldResponse,
  FormRef,
  InspectionForm,
  InspectionRule,
  RuleAction,
} from './types';

// ---------------------------------------------------------------------------
// Public contract
// ---------------------------------------------------------------------------

/**
 * @contract PreviewTelemetry
 *
 * Purpose:
 * - Canonical-semantics summary of the current preview session state.
 *
 * Fields:
 * - visibleFilledCount: fields that are both non-empty AND whose form,
 *   section, and self are not hidden by rules.
 * - hiddenFormCount / hiddenSectionCount / hiddenFieldCount: counts of
 *   entities currently hidden by template-scope + form-scope rules
 *   (template-scope overrides default-hidden entities via `show*`).
 * - requiredEmptyByFormId: per-form list of fieldIds that are required
 *   (either by template-scope `require` action, form-scope rule, or the
 *   field's own `required: true`) AND currently empty AND visible.
 * - loggedViolationCount / contactChangeCount: inspector-side module
 *   counts; the caller passes these in since they are not part of
 *   `AllFormValues`.
 * - totalActivity: sum of visibleFilledCount + loggedViolationCount +
 *   contactChangeCount — the headline number the panel shows.
 *
 * Rules:
 * - Deterministic: same inputs always yield the same output.
 * - Never throws; malformed rules are swallowed by the underlying
 *   evaluators.
 *
 * Sync:
 * - Computed only. Nothing here is persisted.
 */
export interface PreviewTelemetry {
  visibleFilledCount: number;
  hiddenFormCount: number;
  hiddenSectionCount: number;
  hiddenFieldCount: number;
  requiredEmptyByFormId: Record<string, string[]>;
  loggedViolationCount: number;
  contactChangeCount: number;
  totalActivity: number;
}

export interface PreviewTelemetryInput {
  /** The template being previewed — for its `rules` and `formRefs`. */
  templateRules?: readonly InspectionRule[] | readonly { when: Condition; then: RuleAction[] }[] | null;
  /** Ordered form refs from the template. */
  formRefs: readonly FormRef[];
  /** Forms actually available in Dexie / the preview, keyed by formId. */
  forms: ReadonlyMap<string, InspectionForm>;
  /** The renderer's raw session state. */
  allValues: AllFormValues;
  /** Inspector-module counts. */
  loggedViolationCount: number;
  contactChangeCount: number;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function valuesToFieldResponses(values: Record<string, unknown>): Record<string, FieldResponse> {
  const out: Record<string, FieldResponse> = {};
  for (const [fieldId, value] of Object.entries(values)) {
    out[fieldId] = { value };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * @process buildPreviewTelemetry
 *
 * Steps:
 * 1. Run `evaluateTemplateRules` over the whole session state.
 * 2. Build the set of visible form IDs (respecting `showForm` overrides).
 * 3. For each visible form, run `evaluateFormRules` to get per-form
 *    resolved state (hidden sections / fields + required promotion).
 * 4. Walk the fields:
 *    - Count `visibleFilledCount` when value is non-empty AND not hidden
 *      by template- or form-scope rules AND parent section/form visible.
 *    - Collect required-but-empty fieldIds for `requiredEmptyByFormId`.
 * 5. Tally hidden counts.
 * 6. Compose the telemetry record.
 *
 * Inputs: `PreviewTelemetryInput`.
 *
 * Outputs: `PreviewTelemetry` — ready for the right-side panel to render.
 *
 * Failure Handling:
 * - Missing form records (formRef present, form absent in Dexie) are
 *   skipped silently; the preview renderer already communicates that
 *   state to the user visually.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function buildPreviewTelemetry(input: PreviewTelemetryInput): PreviewTelemetry {
  const {
    templateRules,
    formRefs,
    forms,
    allValues,
    loggedViolationCount,
    contactChangeCount,
  } = input;

  const templateResult: TemplateRuleEvalResult = evaluateTemplateRules(
    templateRules ?? [],
    allValues,
  );

  // Establish visible forms — template-scope `showForm` overrides `hideForm`.
  const visibleFormIds = new Set<string>();
  for (const ref of formRefs) {
    if (templateResult.shownForms.has(ref.formId)) {
      visibleFormIds.add(ref.formId);
      continue;
    }
    if (templateResult.hiddenForms.has(ref.formId)) continue;
    visibleFormIds.add(ref.formId);
  }

  let visibleFilledCount = 0;
  let hiddenSectionCount = templateResult.hiddenSections.size;
  let hiddenFieldCount = templateResult.hiddenFields.size;
  const requiredEmptyByFormId: Record<string, string[]> = {};

  for (const ref of formRefs) {
    const form = forms.get(ref.formId);
    if (!form) continue;
    const formValues = allValues[ref.formId] ?? {};
    const responses = valuesToFieldResponses(formValues);
    const resolved = evaluateFormRules(form, responses);

    // Merge template-scope hides so a field hidden at template scope counts
    // only once even if the form engine also marks it hidden.
    const hiddenFieldIds = new Set<string>(
      Object.entries(resolved.fields)
        .filter(([, state]) => state.hidden)
        .map(([fieldId]) => fieldId),
    );
    const hiddenSectionIds = new Set<string>(
      Object.entries(resolved.sections)
        .filter(([, state]) => state.hidden)
        .map(([sectionId]) => sectionId),
    );
    for (const fid of templateResult.hiddenFields) hiddenFieldIds.add(fid);
    for (const sid of templateResult.hiddenSections) hiddenSectionIds.add(sid);

    // Map fieldId -> its parent section (if any) so we can respect
    // section visibility when counting visible-filled.
    const fieldToSection = new Map<string, string>();
    for (const section of form.layout.sections) {
      for (const fieldId of section.fieldIds) {
        fieldToSection.set(fieldId, section.id);
      }
    }

    const formIsHidden = !visibleFormIds.has(ref.formId);
    const perFormRequiredEmpty: string[] = [];

    for (const field of form.fields) {
      // Layout-only field types never carry a value.
      if (field.type === 'section' || field.type === 'instructions') continue;

      const parentSectionId = fieldToSection.get(field.id);
      const sectionIsHidden = parentSectionId
        ? hiddenSectionIds.has(parentSectionId)
        : false;
      const fieldIsHidden = hiddenFieldIds.has(field.id);
      const isVisible = !formIsHidden && !sectionIsHidden && !fieldIsHidden;

      const value = formValues[field.id];
      const empty = isEmpty(value);

      if (isVisible && !empty) {
        visibleFilledCount += 1;
      }

      const resolvedField = resolved.fields[field.id];
      const isRequired =
        resolvedField?.required === true
        || templateResult.requiredFields.has(field.id)
        || (field.required === true);

      if (isVisible && isRequired && empty) {
        perFormRequiredEmpty.push(field.id);
      }
    }

    if (perFormRequiredEmpty.length > 0) {
      requiredEmptyByFormId[ref.formId] = perFormRequiredEmpty;
    }

    // Hidden-section / hidden-field counts include per-form contributions
    // NOT already counted at template scope. Use set-subtraction.
    for (const sid of hiddenSectionIds) {
      if (!templateResult.hiddenSections.has(sid)) hiddenSectionCount += 1;
    }
    for (const fid of hiddenFieldIds) {
      if (!templateResult.hiddenFields.has(fid)) hiddenFieldCount += 1;
    }
  }

  const hiddenFormCount = templateResult.hiddenForms.size;
  const totalActivity = visibleFilledCount + loggedViolationCount + contactChangeCount;

  return {
    visibleFilledCount,
    hiddenFormCount,
    hiddenSectionCount,
    hiddenFieldCount,
    requiredEmptyByFormId,
    loggedViolationCount,
    contactChangeCount,
    totalActivity,
  };
}
